import { useState, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

export default function BookCallPage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Convert FormData to object with correct field names for Edge Function
    const bookingData = {
      name: formData.get('name') as string,
      email: formData.get('email') as string,
      phone: formData.get('phone') as string,
      date: formData.get('preferred_date') as string,
      time: formData.get('preferred_time') as string,
      timezone: formData.get('timezone') as string,
      propertyAddress: formData.get('property_address') as string,
      notes: formData.get('notes') as string,
    };

    try {
      // Submit to Readdy form system
      const formResponse = await fetch('https://readdy.ai/api/form/d69t3hlbd17lru29lfc0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      // Send email notification via Supabase Edge Function
      const { data, error } = await supabase.functions.invoke('send-booking-email', {
        body: bookingData,
      });

      if (error) {
        console.error('Email sending error:', error);
        throw error;
      }

      if (formResponse.ok && data?.success) {
        setSubmitStatus('success');
        form.reset();
        navigate('/book-call/confirmation', {
          state: {
            name: bookingData.name,
            preferred_date: bookingData.date,
            preferred_time: bookingData.time,
          },
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <Link to="/" className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Home
          </Link>
        </div>
      </header>
      
      <section className="pt-12 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Book Your Free Intro Call
            </h1>
            <p className="text-lg text-gray-600">
              Let's discuss your property and how we can help you navigate short-term rental regulations. 
              Choose your preferred date and time, and we'll reach out to confirm.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-gray-100">
            <form id="booking-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                  Full Name <span className="text-[#882629]">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#882629] focus:border-transparent text-sm"
                  placeholder="John Smith"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                  Email Address <span className="text-[#882629]">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#882629] focus:border-transparent text-sm"
                  placeholder="john@example.com"
                />
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                  Phone Number <span className="text-[#882629]">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#882629] focus:border-transparent text-sm"
                  placeholder="(778) 386-9229"
                />
              </div>

              {/* Preferred Date */}
              <div>
                <label htmlFor="preferred_date" className="block text-sm font-semibold text-gray-900 mb-2">
                  Preferred Date <span className="text-[#882629]">*</span>
                </label>
                <input
                  type="date"
                  id="preferred_date"
                  name="preferred_date"
                  required
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#882629] focus:border-transparent text-sm"
                />
              </div>

              {/* Preferred Time */}
              <div>
                <label htmlFor="preferred_time" className="block text-sm font-semibold text-gray-900 mb-2">
                  Preferred Time <span className="text-[#882629]">*</span>
                </label>
                <select
                  id="preferred_time"
                  name="preferred_time"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#882629] focus:border-transparent text-sm"
                >
                  <option value="">Select a time</option>
                  <option value="9:00 AM - 10:00 AM">9:00 AM - 10:00 AM</option>
                  <option value="10:00 AM - 11:00 AM">10:00 AM - 11:00 AM</option>
                  <option value="11:00 AM - 12:00 PM">11:00 AM - 12:00 PM</option>
                  <option value="12:00 PM - 1:00 PM">12:00 PM - 1:00 PM</option>
                  <option value="1:00 PM - 2:00 PM">1:00 PM - 2:00 PM</option>
                  <option value="2:00 PM - 3:00 PM">2:00 PM - 3:00 PM</option>
                  <option value="3:00 PM - 4:00 PM">3:00 PM - 4:00 PM</option>
                  <option value="4:00 PM - 5:00 PM">4:00 PM - 5:00 PM</option>
                  <option value="5:00 PM - 6:00 PM">5:00 PM - 6:00 PM</option>
                </select>
              </div>

              {/* Timezone */}
              <div>
                <label htmlFor="timezone" className="block text-sm font-semibold text-gray-900 mb-2">
                  Your Timezone <span className="text-[#882629]">*</span>
                </label>
                <select
                  id="timezone"
                  name="timezone"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#882629] focus:border-transparent text-sm"
                >
                  <option value="">Select timezone</option>
                  <option value="Pacific Time (PT)">Pacific Time (PT)</option>
                  <option value="Mountain Time (MT)">Mountain Time (MT)</option>
                  <option value="Central Time (CT)">Central Time (CT)</option>
                  <option value="Eastern Time (ET)">Eastern Time (ET)</option>
                  <option value="Atlantic Time (AT)">Atlantic Time (AT)</option>
                </select>
              </div>

              {/* Property Address */}
              <div>
                <label htmlFor="property_address" className="block text-sm font-semibold text-gray-900 mb-2">
                  Property Address
                </label>
                <input
                  type="text"
                  id="property_address"
                  name="property_address"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#882629] focus:border-transparent text-sm"
                  placeholder="123 Main St, Vancouver, BC"
                />
              </div>

              {/* Additional Notes */}
              <div>
                <label htmlFor="notes" className="block text-sm font-semibold text-gray-900 mb-2">
                  What would you like to discuss?
                </label>
                <textarea
                  id="notes"
                  name="notes"
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#882629] focus:border-transparent text-sm resize-none"
                  placeholder="Tell us about your property and any specific questions you have..."
                />
                <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#882629] text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-[#6d1e21] transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Booking...' : 'Book My Free Call'}
              </button>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                  <strong>Success!</strong> Your call has been booked. We'll send you a confirmation email shortly and reach out to confirm the time.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                  <strong>Error:</strong> Something went wrong. Please try again or contact us at{' '}
                  <a href="mailto:contact@furnishedrentalmanagement.com" className="underline">
                    contact@furnishedrentalmanagement.com
                  </a>
                </div>
              )}
            </form>
          </div>

          {/* Contact Info */}
          <div className="text-center mt-8 text-gray-600">
            <p className="text-sm">
              Prefer to call us directly?{' '}
              <a href="tel:7783869229" className="text-[#882629] font-semibold hover:underline">
                (778) 386-9229
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#505A5A] text-white py-8">
        <div className="max-w-4xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-gray-300">
            &copy; 2025 Compliant Rental Management. All rights reserved.
          </p>
          <a
            href="https://readdy.ai/?ref=logo"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="text-sm text-gray-300 hover:text-white transition-colors"
          >
            Powered by Readdy
          </a>
        </div>
      </footer>
    </div>
  );
}
